import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import { Gauge, Link2, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';
import {
    Badge, Button, DataTable, FormActions, FormDrawer, FormSection, IconButton,
    InfoBar, Input, Modal, PageHeader, Select, Spin, TableStatusBar, Textarea, useAlert
} from '../ui';
import { DataTableColumn } from '../ui/DataTable';

const TYPE_KPI = ['COMPTEUR', 'SOMME', 'MOYENNE', 'RATIO'];
const UNITES = ['NOMBRE', 'DUREE', 'MONTANT', 'POURCENTAGE'];
const VISUS = ['KPI', 'COURBE', 'BARRE', 'JAUGE', 'TABLEAU'];
const FREQUENCES = ['REALTIME', 'HORAIRE', 'QUOTIDIEN'];
const STATUTS = ['PUBLIE', 'BROUILLON', 'INACTIF'];
const TYPES_CIBLE = ['ABONNE', 'FILIALE', 'SERVICE', 'ROLE', 'UTILISATEUR'];

const STATUT_COLOR: Record<string, 'green' | 'amber' | 'gray'> = {
    PUBLIE: 'green', BROUILLON: 'amber', INACTIF: 'gray',
};

const OPT = (arr: string[]) => arr.map(v => ({ value: v, label: v }));

/**
 * Gestion BI — catalogue des KPI + affectations par cible.
 * Consomme : GET Kpis (LIST/GET), POST ManageKpi (CREATE/UPDATE/DELETE),
 * GET Affectations (LIST), POST ManageAffectation (SET/DELETE), GET KpiValeur.
 */
export default function GestionBI() {

    const authHeader = useAuthHeader()
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [dataTri, setDataTri] = useState<any[]>([])
    const [selected, setSelected] = useState<any>()

    // Référentiels pour les cibles d'affectation
    const [filiales, setFiliales] = useState<any[]>([])
    const [services, setServices] = useState<any[]>([])
    const [users, setUsers] = useState<any[]>([])
    const [roles, setRoles] = useState<any[]>([])

    // Drawer KPI (create/edit)
    const [openForm, setOpenForm] = useState(false)
    const [editKpi, setEditKpi] = useState<any>(null)
    const [saving, setSaving] = useState(false)
    const [fCode, setFCode] = useState('')
    const [fLibelle, setFLibelle] = useState('')
    const [fType, setFType] = useState('COMPTEUR')
    const [fSource, setFSource] = useState('')
    const [fUnite, setFUnite] = useState('NOMBRE')
    const [fVisu, setFVisu] = useState('KPI')
    const [fFreq, setFFreq] = useState('HORAIRE')
    const [fStatut, setFStatut] = useState('PUBLIE')
    const [fParams, setFParams] = useState('{}')
    const [fOrdre, setFOrdre] = useState('0')

    // Delete KPI
    const [openDelete, setOpenDelete] = useState(false)
    const [toDelete, setToDelete] = useState<any>(null)
    const [deleting, setDeleting] = useState(false)

    // Drawer affectations
    const [openAff, setOpenAff] = useState(false)
    const [affKpi, setAffKpi] = useState<any>(null)
    const [affectations, setAffectations] = useState<any[]>([])
    const [loadingAff, setLoadingAff] = useState(false)
    const [aType, setAType] = useState('FILIALE')
    const [aCible, setACible] = useState('')
    const [aParams, setAParams] = useState('{}')
    const [aOrdre, setAOrdre] = useState('0')
    const [aExcl, setAExcl] = useState(false)
    const [savingAff, setSavingAff] = useState(false)

    // Test de valeur
    const [openTest, setOpenTest] = useState(false)
    const [testKpi, setTestKpi] = useState<any>(null)
    const [testResult, setTestResult] = useState<any>(null)
    const [testing, setTesting] = useState(false)

    useEffect(() => {
        LoadData()
        LoadReferentiels()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /* ---------- Helpers API ---------- */
    function getBI(url: string, payload: any) {
        return axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + url + Crypter.encrypt(JSON.stringify(payload)),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        }).then((r: any) => JSON.parse(Crypter.decrypt(r.data)).Value)
    }

    function postBI(url: string, payload: any) {
        return axios.request({
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + url,
            headers: { 'Content-Type': 'application/json', 'Authorization': authHeader() },
            data: Crypter.encrypt(JSON.stringify(payload)),
        }).then((r: any) => JSON.parse(Crypter.decrypt(r.data)).Value)
    }

    /* ---------- Catalogue ---------- */
    function LoadData() {
        setLoading(true)
        getBI(datas.API_LINKs.BI.Kpis, { Option: 'LIST' })
            .then((res: any) => {
                setLoading(false)
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    const list = Array.isArray(res.Result) ? res.Result
                        : Array.isArray(res.Result?.Kpis) ? res.Result.Kpis
                        : Array.isArray(res.Result?.[0]) ? res.Result[0] : []
                    setDataTri(list.map((k: any, i: number) => ({ ...k, id: k.CodeKpi || i })))
                } else {
                    setDataTri([])
                    alert.warning(res?.Message)
                }
            })
            .catch((error: any) => { setLoading(false); alert.error('Error', error?.message || error) })
    }

    function LoadReferentiels() {
        // Filiales
        axios.request({
            method: 'get', maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetAllFiliales + Crypter.encrypt(user.CodeAbonne),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        }).then((r: any) => {
            const res = JSON.parse(Crypter.decrypt(r.data)).Value
            if (res?.StatutCode == 200) setFiliales(res.Result || [])
        }).catch(() => { })
        // Services
        axios.request({
            method: 'get', maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetAllServices + Crypter.encrypt(user.CodeAbonne),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        }).then((r: any) => {
            const res = JSON.parse(Crypter.decrypt(r.data)).Value
            if (res?.StatutCode == 200) setServices(res.Result || [])
        }).catch(() => { })
        // Utilisateurs
        axios.request({
            method: 'get', maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetAllUser + Crypter.encrypt(user.CodeAbonne),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        }).then((r: any) => {
            const res = JSON.parse(Crypter.decrypt(r.data)).Value
            if (res?.StatutCode == 200) setUsers(Array.isArray(res.Result) ? res.Result : res.Result?.[0] || [])
        }).catch(() => { })
        // Rôles (groupes)
        axios.request({
            method: 'get', maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GroupeHabilitations,
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader(), 'CipherCodeU': Crypter.encrypt(user.CodeU) },
        }).then((r: any) => {
            const res = JSON.parse(Crypter.decrypt(r.data)).Value
            if (res?.StatutCode == 200) setRoles(res.Result?.[0] || [])
        }).catch(() => { })
    }

    /* ---------- Form KPI ---------- */
    function openCreate() {
        setEditKpi(null)
        setFCode(''); setFLibelle(''); setFType('COMPTEUR'); setFSource('')
        setFUnite('NOMBRE'); setFVisu('KPI'); setFFreq('HORAIRE'); setFStatut('PUBLIE')
        setFParams('{}'); setFOrdre('0')
        setOpenForm(true)
    }

    function openEdit(row: any) {
        setEditKpi(row)
        setFCode(row.CodeKpi || '')
        setFLibelle(row.Libelle || '')
        setFType(row.TypeKpi || 'COMPTEUR')
        setFSource(row.SourceName || '')
        setFUnite(row.Unite || 'NOMBRE')
        setFVisu(row.VisuDefaut || 'KPI')
        setFFreq(row.Frequence || 'HORAIRE')
        setFStatut(row.Statut || 'PUBLIE')
        setFParams(row.ParamsDefaut || '{}')
        setFOrdre(String(row.OrdreDefaut ?? 0))
        setOpenForm(true)
    }

    function saveKpi() {
        if (!fCode.trim() || !fLibelle.trim()) { alert.warning('CodeKpi et Libellé sont requis'); return; }
        try { JSON.parse(fParams || '{}') } catch { alert.warning('ParamsDefaut doit être un JSON valide'); return; }
        setSaving(true)
        postBI(datas.API_LINKs.BI.ManageKpi, {
            Option: editKpi ? 'UPDATE' : 'CREATE',
            CodeKpi: fCode.trim(),
            Libelle: fLibelle.trim(),
            TypeKpi: fType,
            SourceName: fSource.trim() || undefined,
            Unite: fUnite,
            VisuDefaut: fVisu,
            Frequence: fFreq,
            Statut: fStatut,
            ParamsDefaut: fParams,
            OrdreDefaut: Number(fOrdre) || 0,
        })
            .then((res: any) => {
                setSaving(false)
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    alert.success(res?.Message || 'KPI enregistré')
                    setOpenForm(false)
                    LoadData()
                } else alert.warning(res?.Message || 'Enregistrement refusé')
            })
            .catch((error: any) => { setSaving(false); alert.error('Error', error?.message || error) })
    }

    function confirmDelete() {
        if (!toDelete) return
        setDeleting(true)
        postBI(datas.API_LINKs.BI.ManageKpi, { Option: 'DELETE', CodeKpi: toDelete.CodeKpi })
            .then((res: any) => {
                setDeleting(false)
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    alert.success(res?.Message || 'KPI désactivé')
                    setOpenDelete(false); setToDelete(null)
                    LoadData()
                } else alert.warning(res?.Message || 'Suppression refusée')
            })
            .catch((error: any) => { setDeleting(false); alert.error('Error', error?.message || error) })
    }

    /* ---------- Affectations ---------- */
    function openAffectations(row: any) {
        setAffKpi(row)
        setAType('FILIALE'); setACible(''); setAParams('{}'); setAOrdre('0'); setAExcl(false)
        setOpenAff(true)
        loadAffectations(row.CodeKpi)
    }

    function loadAffectations(codeKpi: string) {
        setLoadingAff(true)
        getBI(datas.API_LINKs.BI.Affectations, { Option: 'LIST', CodeKpi: codeKpi })
            .then((res: any) => {
                setLoadingAff(false)
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    const list = Array.isArray(res.Result) ? res.Result
                        : Array.isArray(res.Result?.Affectations) ? res.Result.Affectations
                        : Array.isArray(res.Result?.[0]) ? res.Result[0] : []
                    setAffectations(list)
                } else setAffectations([])
            })
            .catch(() => { setLoadingAff(false); setAffectations([]) })
    }

    /** Options de la cible selon le type choisi. */
    function cibleOptions() {
        switch (aType) {
            case 'ABONNE': return [{ value: user.CodeAbonne, label: `Abonné — ${user.CodeAbonne}` }]
            case 'FILIALE': return filiales.map((f: any) => ({ value: f.CodeFiliale, label: `${f.CodeFiliale} - ${f.Libelle || ''}` }))
            case 'SERVICE': return services.map((s: any) => ({ value: s.CodeS, label: `${s.CodeS} - ${s.Libelle || ''}` }))
            case 'ROLE': return roles.map((r: any) => ({ value: String(r.RoleId ?? r.CodeRole ?? r.CodeU), label: `${r.RoleId ?? ''} - ${r.Libelle || ''}` }))
            case 'UTILISATEUR': return users.map((u: any) => ({ value: u.CodeU, label: `${u.CodeU} - ${u.Name || u.Libelle || ''}` }))
            default: return []
        }
    }

    function saveAffectation() {
        if (!aCible) { alert.warning('Sélectionnez une cible'); return; }
        try { JSON.parse(aParams || '{}') } catch { alert.warning('ParamsSurcharge doit être un JSON valide'); return; }
        setSavingAff(true)
        postBI(datas.API_LINKs.BI.ManageAffectation, {
            Option: 'SET',
            CodeKpi: affKpi?.CodeKpi,
            TypeCible: aType,
            CibleId: aCible,
            ParamsSurcharge: aParams,
            OrdreAffichage: Number(aOrdre) || 0,
            EstExclusion: aExcl,
        })
            .then((res: any) => {
                setSavingAff(false)
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    alert.success(res?.Message || 'Affectation enregistrée')
                    setACible(''); setAParams('{}'); setAOrdre('0'); setAExcl(false)
                    loadAffectations(affKpi.CodeKpi)
                } else alert.warning(res?.Message || 'Enregistrement refusé')
            })
            .catch((error: any) => { setSavingAff(false); alert.error('Error', error?.message || error) })
    }

    function deleteAffectation(aff: any) {
        setSavingAff(true)
        postBI(datas.API_LINKs.BI.ManageAffectation, {
            Option: 'DELETE',
            AffectationId: aff.AffectationId ?? aff.Id,
        })
            .then((res: any) => {
                setSavingAff(false)
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    alert.success(res?.Message || 'Affectation retirée')
                    loadAffectations(affKpi.CodeKpi)
                } else alert.warning(res?.Message || 'Suppression refusée')
            })
            .catch((error: any) => { setSavingAff(false); alert.error('Error', error?.message || error) })
    }

    /* ---------- Test de valeur ---------- */
    function testValeur(row: any) {
        setTestKpi(row)
        setTestResult(null)
        setTesting(true)
        setOpenTest(true)
        getBI(datas.API_LINKs.BI.KpiValeur, { CodeKpi: row.CodeKpi })
            .then((res: any) => {
                setTesting(false)
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) setTestResult(res.Result)
                else { setTestResult(null); alert.warning(res?.Message || 'KPI non résolu dans ce contexte') }
            })
            .catch((error: any) => { setTesting(false); alert.error('Error', error?.message || error) })
    }

    const columns: DataTableColumn[] = [
        { field: 'CodeKpi', headerName: 'Code', width: 180 },
        { field: 'Libelle', headerName: 'Libellé', width: 200 },
        {
            field: 'TypeKpi', headerName: 'Type', width: 100,
            renderCell: ({ value }) => <Badge color="blue">{value}</Badge>,
        },
        { field: 'Unite', headerName: 'Unité', width: 100 },
        { field: 'VisuDefaut', headerName: 'Visu', width: 90 },
        { field: 'Frequence', headerName: 'Fréquence', width: 110 },
        {
            field: 'Statut', headerName: 'Statut', width: 100,
            renderCell: ({ value }) => <Badge color={STATUT_COLOR[value] || 'gray'}>{value}</Badge>,
        },
        {
            field: 'Actions', headerName: 'Actions', width: 160, sortable: false,
            renderCell: ({ row }) => (
                <div className="flex items-center gap-1">
                    <IconButton tooltip="Modifier" color="warning" size={30}
                        onClick={(e) => { e.stopPropagation(); openEdit(row) }}>
                        <Pencil size={15} />
                    </IconButton>
                    <IconButton tooltip="Affectations" color="primary" size={30}
                        onClick={(e) => { e.stopPropagation(); openAffectations(row) }}>
                        <Link2 size={15} />
                    </IconButton>
                    <IconButton tooltip="Tester la valeur" color="success" size={30}
                        onClick={(e) => { e.stopPropagation(); testValeur(row) }}>
                        <Play size={15} />
                    </IconButton>
                    <IconButton tooltip="Désactiver" color="danger" size={30}
                        onClick={(e) => { e.stopPropagation(); setToDelete(row); setOpenDelete(true) }}>
                        <Trash2 size={15} />
                    </IconButton>
                </div>
            ),
        },
    ];

    return (
        <Spin spinning={loading}>
            <PageHeader
                title="Catalogue BI — KPI"
                subtitle="Indicateurs de pilotage, affectations par cible et cascade ABONNE > FILIALE > SERVICE > ROLE > UTILISATEUR"
                actions={
                    <Button variant='primary' startIcon={<Plus size={16} />} onClick={openCreate}>
                        Nouveau KPI
                    </Button>
                }
            />

            <div className="eb-table-wrap">
                <DataTable
                    onRowClick={(row: any) => setSelected(dataTri.find((r: any) => r.id == row.id))}
                    rows={dataTri}
                    height={320}
                    columns={columns}
                    searchable
                    emptyText="Aucun KPI dans le catalogue"
                />
            </div>
            <TableStatusBar count={dataTri.length} loading={loading} onRefresh={LoadData} />
            <InfoBar data={[
                { label: 'Code', value: selected?.CodeKpi },
                { label: 'Type', value: selected?.TypeKpi },
                { label: 'Source', value: selected?.SourceName },
                { label: 'Ordre', value: selected?.OrdreDefaut },
            ]} />

            {/* Drawer KPI — create / update */}
            <FormDrawer
                open={openForm}
                onClose={() => setOpenForm(false)}
                title={editKpi ? `Modifier ${editKpi.CodeKpi}` : 'Nouveau KPI'}
                hint="Le CodeAbonne est résolu côté serveur depuis le JWT."
                footer={
                    <Button variant='primary' loading={saving} onClick={saveKpi} startIcon={<Gauge size={16} />}>
                        Enregistrer
                    </Button>
                }
            >
                <FormSection title="Identification" subtitle="Code unique et libellé du KPI">
                    <Input label="Code KPI *" value={fCode} onChange={(e: any) => setFCode(e.target.value)}
                        disabled={!!editKpi} placeholder="KPI-TICKETS-JOUR" />
                    <Input label="Libellé *" value={fLibelle} onChange={(e: any) => setFLibelle(e.target.value)}
                        placeholder="Tickets du jour" />
                    <Input label="Source (procédure stockée)" value={fSource} onChange={(e: any) => setFSource(e.target.value)}
                        placeholder="Sp_IBG_eBCall_BI_TicketsJour" />
                </FormSection>
                <FormSection title="Mesure" subtitle="Type de calcul et unité de restitution">
                    <Select label="Type" value={fType} onChange={setFType} options={OPT(TYPE_KPI)} />
                    <Select label="Unité" value={fUnite} onChange={setFUnite} options={OPT(UNITES)} />
                    <Select label="Fréquence" value={fFreq} onChange={setFFreq} options={OPT(FREQUENCES)} />
                </FormSection>
                <FormSection title="Affichage" subtitle="Visualisation par défaut et publication">
                    <Select label="Visu par défaut" value={fVisu} onChange={setFVisu} options={OPT(VISUS)} />
                    <Select label="Statut" value={fStatut} onChange={setFStatut} options={OPT(STATUTS)} />
                    <Input label="Ordre" type="number" value={fOrdre} onChange={(e: any) => setFOrdre(e.target.value)} />
                    <div className="full">
                        <Textarea label="Paramètres par défaut (JSON)" value={fParams} rows={3}
                            onChange={(e: any) => setFParams(e.target.value)} placeholder='{"Periode":"JOUR"}' />
                    </div>
                </FormSection>
            </FormDrawer>

            {/* Drawer affectations */}
            <FormDrawer
                open={openAff}
                onClose={() => { setOpenAff(false); setAffKpi(null) }}
                title={`Affectations — ${affKpi?.CodeKpi || ''}`}
                hint="Cascade : ABONNE < FILIALE < SERVICE < ROLE < UTILISATEUR. Une exclusion retire le KPI hérité pour la cible."
                width={560}
            >
                <Spin spinning={loadingAff || savingAff}>
                    <FormSection title="Affectations actives" subtitle="Liste des cibles recevant ce KPI">
                        <div className="full flex flex-col gap-1.5">
                            {affectations.length === 0 && (
                                <p className="text-sm text-[var(--eb-text-muted)]">Aucune affectation — le KPI n'est visible par personne.</p>
                            )}
                            {affectations.map((a: any, i: number) => (
                                <div key={a.AffectationId ?? a.Id ?? i}
                                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-[var(--eb-border)]">
                                    <Badge color={a.EstExclusion ? 'red' : 'violet'}>{a.TypeCible}</Badge>
                                    <span className="flex-1 text-sm text-[var(--eb-text)] font-medium">{a.CibleId}</span>
                                    {a.EstExclusion ? <Badge color="red">Exclusion</Badge> : <Badge color="gray">Ordre {a.OrdreAffichage ?? 0}</Badge>}
                                    <IconButton tooltip="Retirer" color="danger" size={26} onClick={() => deleteAffectation(a)}>
                                        <Trash2 size={13} />
                                    </IconButton>
                                </div>
                            ))}
                        </div>
                    </FormSection>

                    <FormSection title="Nouvelle affectation" subtitle="Affecter ou exclure ce KPI pour une cible">
                        <Select label="Type de cible" value={aType}
                            onChange={(v: string) => { setAType(v); setACible(v === 'ABONNE' ? user.CodeAbonne : '') }}
                            options={OPT(TYPES_CIBLE)} />
                        <Select label="Cible *" value={aCible} onChange={setACible}
                            options={[{ value: '', label: '—' }, ...cibleOptions()]} />
                        <Input label="Ordre d'affichage" type="number" value={aOrdre} onChange={(e: any) => setAOrdre(e.target.value)} />
                        <div className="full">
                            <Textarea label="Surcharge de paramètres (JSON)" value={aParams} rows={2}
                                onChange={(e: any) => setAParams(e.target.value)} placeholder='{"SeuilAlerte":50}' />
                        </div>
                        <label className="full inline-flex items-center gap-2 cursor-pointer select-none">
                            <input type="checkbox" checked={aExcl} onChange={(e) => setAExcl(e.target.checked)}
                                className="w-4 h-4 rounded border-[var(--eb-border)] accent-[var(--eb-primary)] cursor-pointer" />
                            <span className="text-sm text-[var(--eb-text)]">Exclusion (retire le KPI hérité pour cette cible)</span>
                        </label>
                    </FormSection>

                    <FormActions>
                        <Button variant='primary' loading={savingAff} onClick={saveAffectation} startIcon={<Link2 size={15} />}>
                            Affecter
                        </Button>
                    </FormActions>
                </Spin>
            </FormDrawer>

            {/* Delete KPI */}
            <Modal
                open={openDelete}
                onClose={() => { setOpenDelete(false); setToDelete(null) }}
                title="Désactiver le KPI"
                footer={
                    <>
                        <Button variant="outline" onClick={() => { setOpenDelete(false); setToDelete(null) }}>Annuler</Button>
                        <Button variant="danger" loading={deleting} onClick={confirmDelete} startIcon={<Trash2 size={15} />}>Désactiver</Button>
                    </>
                }
            >
                <p className="text-sm text-[var(--eb-text)]">
                    Désactiver <strong>{toDelete?.CodeKpi}</strong> ({toDelete?.Libelle}) ?
                    Soft delete — l'historique est conservé.
                </p>
            </Modal>

            {/* Test de valeur */}
            <Modal
                open={openTest}
                onClose={() => setOpenTest(false)}
                title={`Valeur — ${testKpi?.CodeKpi || ''}`}
            >
                <Spin spinning={testing}>
                    {testResult !== null ? (
                        <div className="flex flex-col items-center gap-2 py-4">
                            <span className="text-4xl font-extrabold text-[var(--eb-primary)]" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {typeof testResult === 'object' ? (testResult.Valeur ?? testResult.Value ?? JSON.stringify(testResult)) : String(testResult)}
                            </span>
                            <span className="text-sm text-[var(--eb-text-muted)]">{testKpi?.Libelle} · {testKpi?.Unite}</span>
                        </div>
                    ) : (
                        !testing && <p className="text-sm text-[var(--eb-text-muted)]">Aucune valeur retournée — le KPI n'est peut-être pas résolu pour votre contexte.</p>
                    )}
                </Spin>
            </Modal>
        </Spin>
    );
}
